import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Button, List, ListItem, ListItemIcon, ListItemText } from "@mui/material";
import {
  LocalShipping,
  VerifiedUser,
  Home,
  Translate,
  CheckCircle,
  Schedule,
  Calculate,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import PublicHeaderDark from "components/PublicHeaderDark";
import PublicFooterDark from "components/PublicFooterDark";
import RevealOnScroll from "components/RevealOnScroll";
import DarkHeroBackdrop from "components/DarkHeroBackdrop";
import { SG, BG, BODY_FONT, PANEL, MAXW, btnP, btnG, LIGHT_BAND } from "theme/publicDarkTokens";

const ServicesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const services = [
    {
      icon: <LocalShipping sx={{ fontSize: 56, color: "white" }} />,
      accent: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      keyName: "crossBorder",
      anchorId: "svc-cross-border",
      highlight: true,
    },
    {
      icon: <VerifiedUser sx={{ fontSize: 56, color: "white" }} />,
      accent: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      keyName: "claimProtection",
      anchorId: "svc-claim",
      highlight: false,
    },
    {
      icon: <Home sx={{ fontSize: 56, color: "white" }} />,
      accent: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
      keyName: "homeDelivery",
      anchorId: "svc-home-delivery",
      highlight: false,
    },
    {
      icon: <Translate sx={{ fontSize: 56, color: "white" }} />,
      accent: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
      keyName: "englishSupport",
      anchorId: "svc-english",
      highlight: false,
    },
  ];

  const processSteps = [
    { icon: <Schedule sx={{ fontSize: 24 }} />, titleKey: "services.howItWorks.step1.title", descKey: "services.howItWorks.step1.description" },
    { icon: <LocalShipping sx={{ fontSize: 24 }} />, titleKey: "services.howItWorks.step2.title", descKey: "services.howItWorks.step2.description" },
    { icon: <CheckCircle sx={{ fontSize: 24 }} />, titleKey: "services.howItWorks.step3.title", descKey: "services.howItWorks.step3.description" },
  ];

  return (
    <Box sx={{ background: BG, fontFamily: BODY_FONT, overflowX: "hidden" }}>
      <PublicHeaderDark />

      {/* ══════════ HERO ══════════ */}
      <Box component="section" sx={{ position: "relative", overflow: "hidden", background: BG, textAlign: "center" }}>
        <DarkHeroBackdrop />
        <Box sx={{ position: "relative", maxWidth: MAXW, mx: "auto", px: { xs: 2.5, md: 5 }, py: { xs: 8, md: 11 } }}>
          <RevealOnScroll y={28} duration={700}>
            <Typography
              component="h1"
              sx={{
                fontFamily: SG,
                fontSize: "clamp(32px, 4vw, 52px)",
                fontWeight: 700,
                letterSpacing: "-.03em",
                mb: 2.25,
                background: "linear-gradient(130deg, #C084FC 0%, #7C3AED 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              {t("services.title")}
            </Typography>
            <Typography sx={{ fontSize: 17, color: "rgba(248,245,255,.56)", maxWidth: 640, mx: "auto", mb: 5, lineHeight: 1.75 }}>
              {t("services.subtitle")}
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", flexWrap: "wrap" }}>
              <Button sx={btnP} onClick={() => navigate("/calculator")}>
                <Calculate sx={{ fontSize: 17 }} />
                {t("navigation.calculator")}
              </Button>
              <Button sx={btnG} onClick={() => navigate("/contact")}>
                {t("common.contactUs")}
              </Button>
            </Box>
          </RevealOnScroll>
        </Box>
      </Box>

      {/* ══════════ SERVICES GRID ══════════ */}
      <Box component="section" sx={{ background: BG, py: { xs: 6, md: 9 } }}>
        <Box sx={{ maxWidth: MAXW, mx: "auto", px: { xs: 2.5, md: 5 } }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
            {services.map((service, index) => {
              const k = service.keyName;
              const features = [1, 2, 3, 4].map((n) => t(`services.${k}.f${n}`));
              return (
                <Box key={index} id={service.anchorId} sx={{ scrollMarginTop: "90px" }}>
                  <RevealOnScroll y={28} delay={index * 80}>
                    <Box
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: "20px",
                        overflow: "hidden",
                        background: PANEL,
                        border: service.highlight ? "1.5px solid rgba(124,58,237,.45)" : "1.5px solid rgba(248,245,255,.08)",
                        transition: "all .3s ease",
                        "&:hover": { transform: "translateY(-6px)", boxShadow: "0 20px 48px rgba(0,0,0,.35)" },
                      }}
                    >
                      {/* Accent header */}
                      <Box sx={{ background: service.accent, p: 3, display: "flex", alignItems: "center", gap: 2, position: "relative" }}>
                        <Box
                          sx={{
                            width: 72,
                            height: 72,
                            borderRadius: "50%",
                            background: "rgba(255,255,255,.18)",
                            backdropFilter: "blur(8px)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {service.icon}
                        </Box>
                        <Box sx={{ flex: 1, color: "#fff" }}>
                          <Typography sx={{ fontFamily: SG, fontSize: 21, fontWeight: 700 }}>{t(`services.${k}.title`)}</Typography>
                          <Typography sx={{ opacity: 0.95, fontWeight: 600, fontSize: 14, mt: 0.5 }}>{t(`services.${k}.price`)}</Typography>
                        </Box>
                        {service.highlight && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 12,
                              right: 12,
                              bgcolor: "#fff",
                              color: "#6D28D9",
                              fontWeight: 700,
                              fontSize: 12,
                              px: 1.5,
                              py: 0.4,
                              borderRadius: "100px",
                            }}
                          >
                            {t("services.popularBadge")}
                          </Box>
                        )}
                      </Box>

                      <Box sx={{ flexGrow: 1, p: 3 }}>
                        <Typography sx={{ fontSize: 14, color: "rgba(248,245,255,.52)", mb: 2, lineHeight: 1.75 }}>
                          {t(`services.${k}.description`)}
                        </Typography>
                        <List dense disablePadding>
                          {features.map((feature, idx) => (
                            <ListItem key={idx} sx={{ py: 0.5, px: 0 }} disableGutters>
                              <ListItemIcon sx={{ minWidth: 28 }}>
                                <CheckCircle sx={{ fontSize: 18, color: "#4ADE80" }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={feature}
                                sx={{ "& .MuiListItemText-primary": { fontSize: "0.92rem", color: "rgba(248,245,255,.75)" } }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    </Box>
                  </RevealOnScroll>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* ══════════ HOW IT WORKS (light island) ══════════ */}
      <Box component="section" sx={{ background: LIGHT_BAND, py: { xs: 6, md: 10 } }}>
        <Box sx={{ maxWidth: MAXW, mx: "auto", px: { xs: 2.5, md: 5 } }}>
          <RevealOnScroll y={20} sx={{ textAlign: "center", mb: 7 }}>
            <Typography sx={{ fontFamily: SG, fontSize: { xs: 26, md: 34 }, fontWeight: 700, letterSpacing: "-.025em", color: "#0A0618", mb: 1.5 }}>
              {t("services.howItWorks.title")}
            </Typography>
            <Typography sx={{ fontSize: 17, color: "rgba(10,6,24,.55)" }}>{t("services.howItWorks.subtitle")}</Typography>
          </RevealOnScroll>
          <Box sx={{ position: "relative", display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3,1fr)" }, gap: { xs: 5, md: 3 } }}>
            <Box
              sx={{
                display: { xs: "none", md: "block" },
                position: "absolute",
                top: 32,
                left: "16.5%",
                right: "16.5%",
                height: "2px",
                background: "repeating-linear-gradient(90deg, rgba(124,58,237,.4) 0 10px, transparent 10px 18px)",
                zIndex: 0,
              }}
            />
            {processSteps.map((step, index) => (
              <RevealOnScroll key={index} y={24} delay={index * 100}>
                <Box sx={{ position: "relative", zIndex: 1, textAlign: "center" }}>
                  <Box
                    sx={{
                      position: "relative",
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      mx: "auto",
                      mb: 2.25,
                      background: "linear-gradient(135deg, #7C3AED 0%, #C084FC 100%)",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 14px 32px rgba(124,58,237,.35)",
                    }}
                  >
                    {step.icon}
                    <Box
                      sx={{
                        position: "absolute",
                        top: -6,
                        right: -6,
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        background: "#0A0618",
                        color: "#fff",
                        fontSize: 12,
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: `2px solid ${LIGHT_BAND}`,
                      }}
                    >
                      {index + 1}
                    </Box>
                  </Box>
                  <Typography sx={{ fontFamily: SG, fontWeight: 700, fontSize: 17, mb: 1, color: "#0A0618" }}>{t(step.titleKey)}</Typography>
                  <Typography sx={{ fontSize: 14, color: "rgba(10,6,24,.55)", lineHeight: 1.7, maxWidth: 260, mx: "auto" }}>{t(step.descKey)}</Typography>
                </Box>
              </RevealOnScroll>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ══════════ CTA ══════════ */}
      <Box
        component="section"
        sx={{ position: "relative", overflow: "hidden", py: { xs: 8, md: 11 }, textAlign: "center", background: "linear-gradient(140deg, #1A0845 0%, #3B1181 50%, #5B21B6 100%)" }}
      >
        <Box sx={{ position: "absolute", width: 700, height: 700, borderRadius: "50%", border: "1px solid rgba(255,255,255,.04)", top: -350, right: -240, pointerEvents: "none" }} />
        <Box sx={{ position: "relative", maxWidth: 700, mx: "auto", px: { xs: 2.5, md: 5 } }}>
          <RevealOnScroll y={24}>
            <Typography
              component="h2"
              sx={{ fontFamily: SG, fontSize: "clamp(26px, 3vw, 38px)", fontWeight: 700, color: "#fff", letterSpacing: "-.03em", mb: 2 }}
            >
              {t("services.cta.title")}
            </Typography>
            <Typography sx={{ fontSize: 16, color: "rgba(255,255,255,.6)", mb: 4.5 }}>{t("services.cta.subtitle")}</Typography>
            <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", flexWrap: "wrap" }}>
              <Button sx={{ ...btnP, background: "#fff", color: "#3B1181", "&:hover": { background: LIGHT_BAND } }} onClick={() => navigate("/contact")}>
                {t("common.contactUs")}
              </Button>
              <Button sx={{ ...btnG, borderColor: "rgba(255,255,255,.3)" }} onClick={() => navigate("/track")}>
                {t("navigation.trackPackage")}
              </Button>
            </Box>
          </RevealOnScroll>
        </Box>
      </Box>

      <PublicFooterDark />
    </Box>
  );
};

export default ServicesPage;
